import Vue from 'vue';
import { uniqBy, has } from 'lodash-es';

import * as Assets from '@/js/services/assets';

const defaultAssetTypes = ['image', 'video'];
const state = {
  assets: [],
  assetDirectories: [],
  assetPage: 1,
  assetLimit: 30,
  assetTypes: defaultAssetTypes,
  assetSearch: '',
  assetSortBy: 'created_time',
  assetSortDir: 'desc',
  assetDirectory: null,
  assetDirectoryId: null,
  assetTotal: 0,
  isLoadingAssets: false,
  isLoadingTimelineAssets: false,
  assetLookupTable: {},
};

/* eslint-disable no-param-reassign, no-shadow */
const getters = {
  hasMoreAssets: (state) => state.assetTotal > (state.assetPage * state.assetLimit),
  isLoading: (state) => state.isLoadingAssets,
};

const mutations = {
  SET_ASSETS(state, assets) {
    state.assets = uniqBy([...state.assets, ...assets], '_id');
  },

  SET_DIRECTORIES(state, dirs) {
    state.assetDirectories = uniqBy([...state.assetDirectories, ...dirs], '_id');
  },

  ADD_ASSET_TO_TOP(state, asset) {
    state.assets = uniqBy([asset, ...state.assets], '_id');
  },

  SET_STATE_FIELD(state, { key, value }) {
    state[key] = value;
  },

  ADD_TO_LOOKUP(state, asset) {
    asset.layout = 'asset';
    Vue.set(state.assetLookupTable, asset._id, asset);
    // state.assetLookupTable[asset._id] = asset;
  },

  ADD_MANY_TO_LOOKUP(state, assets) {
    assets.forEach((asset) => {
      asset.layout = 'asset';
      Vue.set(state.assetLookupTable, asset._id, asset);
    });
  },
};

const actions = {
  async getAssets({ commit, state }, payload) {
    const { sortBy } = payload || {};

    commit('SET_STATE_FIELD', { key: 'isLoadingAssets', value: true });

    // For UX reasons, we want to show all asset types if none are selected
    const assetTypes = state.assetTypes.length ? state.assetTypes : defaultAssetTypes;

    try {
      const assets = await Assets.getAssets({
        search: state.assetSearch,
        searchAll: true,
        page: state.assetPage,
        limit: state.assetLimit,
        ...(state.assetDirectoryId && { directory: state.assetDirectoryId }),
        sortBy: sortBy || state.assetSortBy,
        sortDir: state.assetSortDir,
        assetTypes,
        stats: true,
        legacySearch: false,
      });

      commit('SET_ASSETS', assets.assets);
      commit('ADD_MANY_TO_LOOKUP', assets.assets);
      commit('SET_STATE_FIELD', { key: 'assetTotal', value: assets.total });
      commit('SET_DIRECTORIES', assets.directories);
    } finally {
      commit('SET_STATE_FIELD', { key: 'isLoadingAssets', value: false });
    }
  },

  resetAssets({ commit }) {
    commit('SET_STATE_FIELD', { key: 'assetPage', value: 1 });
    commit('SET_STATE_FIELD', { key: 'assets', value: [] });
    commit('SET_STATE_FIELD', { key: 'assetDirectories', value: [] });
  },

  getNextPage({ commit, state, dispatch }) {
    commit('SET_STATE_FIELD', { key: 'assetPage', value: state.assetPage + 1 });
    dispatch('getAssets');
  },

  async addUploadedAsset({ commit, state }, asset) {
    if (!state.assetDirectoryId || asset.directory === state.assetDirectoryId) {
      const fullAsset = await Assets.getAsset(asset._id);
      commit('ADD_TO_LOOKUP', fullAsset);
      commit('ADD_ASSET_TO_TOP', asset);
    }
  },

  async getDirectory({ commit }, id) {
    if (!id) {
      commit('SET_STATE_FIELD', { key: 'assetDirectory', value: null });
      return;
    }

    try {
      const directoryData = await Assets.getDirectory(id);
      commit('SET_STATE_FIELD', { key: 'assetDirectory', value: directoryData });
    } catch (error) {
      console.error('Error getting directory', error);
    }
  },

  setDirectory({ commit, dispatch }, directoryId) {
    commit('SET_STATE_FIELD', { key: 'assetDirectoryId', value: directoryId });
    dispatch('getDirectory', directoryId);
    dispatch('resetAssets');
    dispatch('getAssets');
  },

  searchAssets({ commit, dispatch }, search) {
    commit('SET_STATE_FIELD', { key: 'assetSearch', value: search });

    // reset everything
    dispatch('resetAssets');
    commit('SET_STATE_FIELD', { key: 'assetDirectoryId', value: null });

    dispatch('getAssets');
  },

  toggleAssetSortDir({ commit, dispatch }) {
    commit('SET_STATE_FIELD', { key: 'assetSortDir', value: state.assetSortDir === 'asc' ? 'desc' : 'asc' });

    dispatch('resetAssets');
    dispatch('getAssets', { sortBy: 'title' });
  },

  setAssetTypes({ commit, dispatch }, assetTypes) {
    commit('SET_STATE_FIELD', { key: 'assetTypes', value: assetTypes });

    dispatch('resetAssets');
    dispatch('getAssets');
  },

  async getTimelineAssets({ commit, rootState }) {
    const { timeline } = rootState.production.production;

    let assetIds = timeline.reduce((memo, item) => {
      if (item.layout === 'asset') {
        memo.add(item._id);
      }

      return memo;
    }, new Set());

    assetIds = [...assetIds].filter((id) => !has(state.assetLookupTable, id));
    if (!assetIds.length) {
      return;
    }

    commit('SET_STATE_FIELD', { key: 'isLoadingTimelineAssets', value: true });

    const resp = await Assets.getMultiple(assetIds);
    if (resp?.assets?.length) {
      commit('ADD_MANY_TO_LOOKUP', resp.assets);
    }

    commit('SET_STATE_FIELD', { key: 'isLoadingTimelineAssets', value: false });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
